import { useEffect, useRef, useState } from 'react';
import './Features.css';

/* ── Visual: OCR bars ── */
function VisOcr() {
  return (
    <div className="vis-ocr">
      <div className="vis-ocr-line scanning" style={{ width: '100%' }} />
      <div className="vis-ocr-line done"     style={{ width: '85%'  }} />
      <div className="vis-ocr-line scanning" style={{ width: '92%'  }} />
      <div className="vis-ocr-line hindi done" style={{ width: '78%' }} />
      <div className="vis-ocr-line done"     style={{ width: '95%'  }} />
      <div className="vis-ocr-line scanning" style={{ width: '60%'  }} />
      <div className="vis-ocr-label">
        <span>847 words identified</span>
        <span>page 1 / 3</span>
      </div>
    </div>
  );
}

/* ── Visual: Tag confidence bars ── */
const TAG_DATA = [
  { label: 'invoice',  conf: '0.96', fill: 96 },
  { label: 'finance',  conf: '0.91', fill: 91 },
  { label: 'hindi',    conf: '0.88', fill: 88 },
  { label: 'feb-2026', conf: '0.74', fill: 74 },
];

function VisTags() {
  const ref = useRef(null);
  const [triggered, setTriggered] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setTriggered(true); obs.disconnect(); } },
      { threshold: 0.3 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  return (
    <div className="vis-tags" ref={ref}>
      {TAG_DATA.map((t) => (
        <div className="vis-tag-row" key={t.label}>
          <div className="vis-tag-bar">
            <div
              className="vis-tag-fill"
              style={{ width: triggered ? t.fill + '%' : '0%' }}
            />
            <span className="vis-tag-label">{t.label}</span>
          </div>
          <div className="vis-tag-conf">{t.conf}</div>
        </div>
      ))}
    </div>
  );
}

/* ── Visual: Pipe log ── */
const PIPE_LINES = [
  { key: 'READ',   msg: 'invoice.pdf → 1,204 words found' },
  { key: 'SCAN',   msg: 'page_02 → Hindi · 98 characters' },
  { key: 'TAG',    msg: 'category: finance · match: 96%' },
  { key: 'TAG',    msg: 'sub: invoice · match: 91%' },
  { key: 'SAVE',   msg: 'document saved · index updated' },
  { key: 'STATUS', msg: 'memory clear · background done' },
];

function VisPipe() {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setVisible(true); obs.disconnect(); } },
      { threshold: 0.2 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  return (
    <div className="vis-pipe" ref={ref}>
      {PIPE_LINES.map((l, i) => (
        <div
          key={i}
          className="vis-pipe-line"
          style={{
            opacity: visible ? 1 : 0,
            transform: visible ? 'translateX(0)' : 'translateX(-8px)',
            transition: `opacity 0.4s ${i * 0.18}s, transform 0.4s ${i * 0.18}s`,
          }}
        >
          <span>[{l.key}]</span> {l.msg}
        </div>
      ))}
    </div>
  );
}

/* ── Visual: Offline ── */
function VisOffline() {
  return (
    <div className="vis-offline">
      <div className="vis-offline-ring">
        <div className="vis-offline-icon">📴</div>
      </div>
      <div className="vis-offline-status">
        <div className="status-dot" />
        All systems operational
      </div>
    </div>
  );
}

/* ── Feature rows ── */
const FEATURES = [
  {
    num: '01 / 04',
    title: <>On-device <em>text reading</em><br />that doesn't guess.</>,
    desc: <>Text reading happens entirely in the background — the app stays fast and responsive no matter how many pages your document has. Word-level precision, not approximation.</>,
    tag: 'Background processing · On-device · Precise reading',
    visual: <VisOcr />,
  },
  {
    num: '02 / 04',
    title: <>Built for <em>the way India reads.</em></>,
    desc: <><strong>Hindi and English</strong> recognized natively — individually or mixed within the same document. Devanagari script handled without transliteration or external API calls.</>,
    tag: 'Language detection · Zero network · Hindi + English',
    visual: <VisTags />,
  },
  {
    num: '03 / 04',
    title: <>Tags assigned.<br /><em>You stay in control.</em></>,
    desc: <>The app assigns tags with a confidence score. <strong>You can override any of them.</strong> Categories are based on content, language, and document type — all figured out on your phone.</>,
    tag: 'Smart tagging · Confidence scoring · Fully editable',
    visual: <VisPipe />,
  },
  {
    num: '04 / 04',
    title: <>No signal.<br /><em>No problem.</em></>,
    desc: <>No connection required — not for reading, not for tagging, not for search. <strong>TAGpdf was built offline-first from day one</strong>, not retrofitted as a feature.</>,
    tag: 'Offline · On-device · Zero network dependency',
    visual: <VisOffline />,
  },
];

export default function Features() {
  return (
    <section className="features" id="features">
      <div className="section-label reveal">Engineered for precision</div>
      {FEATURES.map((f, i) => (
        <div className="feature-row" key={i}>
          <div className="feature-text">
            <div className="feature-num">{f.num}</div>
            <h3 className="feature-title">{f.title}</h3>
            <p className="feature-desc">{f.desc}</p>
            <div className="feature-tag">{f.tag}</div>
          </div>
          <div className="feature-visual">{f.visual}</div>
        </div>
      ))}
    </section>
  );
}
